import React, { useCallback, useId, useLayoutEffect, useRef } from 'react';
import { alpha, Box, FormHelperText, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

const STORAGE_PREFIX = 'waypoint:requirement-textarea-height:v1:';
const MAX_STORED_HEIGHT = 10000;

export const textareaHeightStorageKey = (resizeKey: string): string =>
  `${STORAGE_PREFIX}${resizeKey}`;

type NativeTextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'rows' | 'style'
> & {
  'data-testid'?: string;
  'data-tour-id'?: string;
};

export interface ResizableRequirementTextareaProps {
  value: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  /** Include both the workflow ID and field name to avoid sharing a manual size. */
  resizeKey: string;
  id?: string;
  label?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  placeholder?: string;
  inputTestId?: string;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  textareaProps?: NativeTextareaProps;
  className?: string;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
}

function storedHeight(resizeKey: string): number | null {
  try {
    const raw = window.localStorage.getItem(textareaHeightStorageKey(resizeKey));
    if (raw === null) return null;
    const height = Number(raw);
    return Number.isFinite(height) && height > 0 && height <= MAX_STORED_HEIGHT ? height : null;
  } catch {
    return null;
  }
}

function saveHeight(resizeKey: string, height: number): void {
  try {
    window.localStorage.setItem(textareaHeightStorageKey(resizeKey), String(Math.round(height)));
  } catch {
    // Editing still works when storage is unavailable or full.
  }
}

function twoLineHeight(textarea: HTMLTextAreaElement): number {
  const styles = window.getComputedStyle(textarea);
  const fontSizeValue = parseFloat(styles.fontSize);
  const fontSize = fontSizeValue > 4 ? fontSizeValue : 16;
  const lineHeightValue = parseFloat(styles.lineHeight);
  const lineHeight = styles.lineHeight.endsWith('px') && lineHeightValue > 0
    ? lineHeightValue
    : lineHeightValue > 0 && lineHeightValue <= 4
      ? lineHeightValue * fontSize
      : fontSize * 1.5;
  const verticalSpace = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
    .reduce((sum, name) => sum + (parseFloat(styles[name as keyof CSSStyleDeclaration] as string) || 0), 0);
  return Math.ceil(2 * lineHeight + verticalSpace);
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') ref(value);
  else if (ref && 'current' in ref) (ref as React.MutableRefObject<T | null>).current = value;
}

/** A native textarea because MUI's multiline TextField overwrites dragged heights after updates. */
export function ResizableRequirementTextarea({
  value, onChange, resizeKey, id, label, helperText, error = false, disabled = false,
  readOnly = false, required = false, placeholder, inputTestId, inputRef,
  textareaProps, className, style, sx,
}: ResizableRequirementTextareaProps) {
  const generatedId = useId();
  const textareaId = id ?? `requirement-description-${generatedId}`;
  const helperId = `${textareaId}-helper`;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const manualHeightRef = useRef<number | null>(null);
  const activeResizeKeyRef = useRef<string | null>(null);
  const pointerCleanupRef = useRef<(() => void) | null>(null);

  const setTextareaRef = useCallback((element: HTMLTextAreaElement | null) => {
    textareaRef.current = element;
    assignRef(inputRef, element);
  }, [inputRef]);

  const syncHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const minimum = twoLineHeight(textarea);
    textarea.style.minHeight = `${minimum}px`;
    if (manualHeightRef.current !== null) {
      textarea.style.height = `${Math.max(minimum, manualHeightRef.current)}px`;
      textarea.style.overflowY = 'auto';
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.max(minimum, textarea.scrollHeight)}px`;
    textarea.style.overflowY = 'hidden';
  }, []);

  useLayoutEffect(() => {
    if (activeResizeKeyRef.current !== resizeKey) {
      activeResizeKeyRef.current = resizeKey;
      manualHeightRef.current = storedHeight(resizeKey);
    }
    syncHeight();
  }, [resizeKey, syncHeight, value]);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const parent = textarea?.parentElement;
    if (!textarea || !parent) return;
    let previousWidth = parent.getBoundingClientRect().width;
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(([entry]) => {
        const nextWidth = entry.contentRect.width;
        if (Math.abs(nextWidth - previousWidth) > 1) {
          previousWidth = nextWidth;
          if (manualHeightRef.current === null) syncHeight();
        }
      });
      observer.observe(parent);
      return () => observer.disconnect();
    }
    const onResize = () => { if (manualHeightRef.current === null) syncHeight(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [resizeKey, syncHeight]);

  useLayoutEffect(() => () => pointerCleanupRef.current?.(), []);

  const onPointerDown = (event: React.PointerEvent<HTMLTextAreaElement>) => {
    textareaProps?.onPointerDown?.(event);
    if (disabled) return;
    const textarea = event.currentTarget;
    const before = textarea.getBoundingClientRect().height || parseFloat(textarea.style.height);
    pointerCleanupRef.current?.();
    const finish = () => {
      pointerCleanupRef.current?.();
      const after = textarea.getBoundingClientRect().height || parseFloat(textarea.style.height);
      if (!Number.isFinite(after) || Math.abs(after - before) <= 2) return;
      const height = Math.max(twoLineHeight(textarea), Math.round(after));
      manualHeightRef.current = height;
      textarea.style.height = `${height}px`;
      textarea.style.overflowY = 'auto';
      saveHeight(resizeKey, height);
    };
    const cleanup = () => {
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      pointerCleanupRef.current = null;
    };
    pointerCleanupRef.current = cleanup;
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  };

  const describedBy = [textareaProps?.['aria-describedby'], helperText ? helperId : null]
    .filter(Boolean).join(' ') || undefined;
  const nativeTestId = textareaProps?.['data-testid'];
  const baseSx = {
    width: '100%', boxSizing: 'border-box', display: 'block', resize: 'vertical',
    px: '14px', py: '16.5px', border: '1px solid', borderRadius: '4px',
    borderColor: error ? 'error.main' : (theme: Theme) => alpha(theme.palette.text.primary, 0.23),
    bgcolor: 'background.paper', color: 'text.primary', fontFamily: 'inherit',
    fontSize: '1rem', lineHeight: 1.5,
    '&:hover': { borderColor: error ? 'error.main' : 'text.primary' },
    '&:focus-visible': { outline: 'none', borderWidth: '2px', borderColor: error ? 'error.main' : 'primary.main' },
    '&:disabled': { cursor: 'not-allowed', opacity: 0.65 },
  };

  return (
    <Box>
      {label && <Typography component="label" htmlFor={textareaId} variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {label}{required && <Typography component="span" color="error.main"> *</Typography>}
      </Typography>}
      <Box
        component="textarea"
        {...textareaProps}
        id={textareaId}
        ref={setTextareaRef}
        rows={2}
        value={value}
        onChange={onChange}
        onPointerDown={onPointerDown}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder}
        className={className}
        style={style}
        data-testid={inputTestId ?? nativeTestId}
        aria-describedby={describedBy}
        sx={Array.isArray(sx) ? [baseSx, ...sx] : [baseSx, sx]}
      />
      {helperText && <FormHelperText id={helperId} error={error}>{helperText}</FormHelperText>}
    </Box>
  );
}

export default ResizableRequirementTextarea;
